import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PrintIcon from "@mui/icons-material/Print";
import { useAuth } from "../../../context/AuthContext";
import BusquedaPorIdentificador from "./BusquedaPorIdentificador";
import {
  crearTraspasoCentroEquipos,
  getTraspasoCentroEquipos,
  getTraspasoPdfCentroEquipos,
  listarAccesoriosTraspasoCentroEquipos,
  listarCentroEquipos,
  listarTraspasosCentroEquipos,
} from "../services/centroEquiposService";

const TIPOS = [
  { value: "equipo_modem", label: "Modems" },
  { value: "equipo_chip", label: "Chips" },
  { value: "equipo_bateria", label: "Baterias" },
  { value: "equipo_radio", label: "Radios" },
  { value: "equipo_varios", label: "Varios" },
  { value: "equipo_accesorio", label: "Accesorios" },
];

const ESTADOS = ["Inactivo", "Almacen", "Programado", "Todos"];

const SUCURSALES_DESTINO = [
  "La Paz",
  "Cochabamba",
  "Santa Cruz",
  "Oruro",
  "Chuquisaca",
  "Pando",
  "Beni",
  "Tarija",
  "Potosi",
  "Yacuiba",
  "La Paz(El Alto)",
  "Uyuni",
];

const SUCURSALES_ORIGEN_EXTRA = [
  "La Paz(El Alto)",
  "Oruro",
  "Chuquisaca",
  "Pando",
  "Beni",
  "Tarija",
  "Potosi",
  "Yacuiba",
  "Uyuni",
];

const controlSx = {
  "& .MuiInputLabel-root": { fontSize: 11 },
  "& .MuiOutlinedInput-root": { height: 30, fontSize: 11.5 },
  "& .MuiSelect-select": { py: 0.35, px: 1 },
};

// Input nativo (Box component="input") en vez de TextField: la tabla de accesorios puede
// tener cientos de filas y el TextField de MUI (label/outline/ripple) la vuelve pesada.
const accInputSx = {
  fontFamily: "inherit",
  fontSize: 11,
  padding: "1px 4px",
  border: "1px solid",
  borderColor: "divider",
  borderRadius: "3px",
  outline: "none",
  bgcolor: "transparent",
  color: "text.primary",
  boxSizing: "border-box",
  display: "block",
  margin: 0,
  height: 20,
  "&:focus": { borderColor: "primary.main" },
  "&:disabled": { color: "text.disabled", bgcolor: "action.hover" },
};

const normalize = (value) =>
  String(value || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase();

const normalizeAccess = (value) => normalize(value);

const fmt = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return String(value);
};

const getId = (row) => row?.identificador || row?.num_sim || row?.numero || row?.serie || row?.id;

const getBusquedaTipo = (tipo) => {
  if (tipo === "equipo_modem") return "modem";
  if (tipo === "equipo_chip") return "chip";
  if (tipo === "equipo_bateria") return "bateria";
  if (tipo === "equipo_radio") return "radio";
  if (tipo === "equipo_varios") return "varios";
  return "todos";
};

const parseBuscarTerms = (value) => Array.from(new Set(
  String(value || "")
    .split(/[\s,;]+/)
    .map((item) => item.trim())
    .filter(Boolean)
)).slice(0, 100);

const normalizeKey = (value) => String(value || "").trim().toUpperCase();

const getRowSearchKeys = (row, tipo) => {
  if (tipo === "equipo_modem") return [row.id_equipo, row.num_sim, row.emai];
  if (tipo === "equipo_chip") return [row.numero, row.emai];
  if (tipo === "equipo_varios") return [row.serie, row.emai];
  return [getId(row), row.serie, row.numero, row.emai];
};

const getNotFoundTerms = (terms, rows, tipo) => {
  if (!terms.length) return [];
  const found = new Set();
  rows.forEach((row) => {
    getRowSearchKeys(row, tipo).forEach((value) => {
      const key = normalizeKey(value);
      if (key) found.add(key);
    });
  });
  return terms.filter((term) => !found.has(normalizeKey(term)));
};

const getTipoLabel = (value) => TIPOS.find((tipoItem) => tipoItem.value === value)?.label || value || "-";

const getColumns = (tipo) => {
  if (tipo === "equipo_modem") {
    return [
      { label: "ID", value: (row) => row.id_equipo },
      { label: "Num SIM", value: (row) => row.num_sim || getId(row) },
      { label: "EMAI", value: (row) => row.emai },
      { label: "Modelo", value: (row) => row.modelo },
      { label: "Estado", value: (row) => row.estado },
      { label: "Ciudad", value: (row) => row.sucursal || row.ciudad },
      { label: "Placa", value: (row) => row.placa },
      { label: "Grupo", value: (row) => row.grupo },
    ];
  }
  if (tipo === "equipo_chip") {
    return [
      { label: "Numero", value: (row) => row.numero || getId(row) },
      { label: "EMAI", value: (row) => row.emai },
      { label: "Modelo", value: (row) => row.modelo },
      { label: "Estado", value: (row) => row.estado },
      { label: "Ciudad", value: (row) => row.sucursal || row.ciudad },
      { label: "Modem", value: (row) => row.modem || row.relacion },
      { label: "Placa", value: (row) => row.placa },
    ];
  }
  if (tipo === "equipo_varios") {
    return [
      { label: "Serie", value: (row) => row.serie || getId(row) },
      { label: "EMAI", value: (row) => row.emai },
      { label: "Modelo", value: (row) => row.modelo },
      { label: "Categoria", value: (row) => row.categoria || row.tipo_equipo },
      { label: "Estado", value: (row) => row.estado },
      { label: "Ciudad", value: (row) => row.sucursal || row.ciudad },
      { label: "Servicio", value: (row) => row.cod_servicio },
    ];
  }
  return [
    { label: "Identificador", value: (row) => getId(row) },
    { label: "Modelo", value: (row) => row.modelo },
    { label: "Estado", value: (row) => row.estado },
    { label: "Ciudad", value: (row) => row.sucursal || row.ciudad },
    { label: "Placa", value: (row) => row.placa },
    { label: "Propiedad", value: (row) => row.propiedad },
    { label: "Asignado", value: (row) => row.asignado },
  ];
};

export default function TraspasoEquipos() {
  const { user } = useAuth();
  const userSucursal = user?.sucursal || "";
  const canManageInventory = useMemo(() => {
    const role = normalizeAccess(user?.role);
    const subRol = normalizeAccess(user?.sub_rol);
    return subRol === "inventario" || role === "admin" || role === "gerente";
  }, [user?.role, user?.sub_rol]);
  const [origen, setOrigen] = useState(userSucursal);
  const origenes = useMemo(() => {
    // "La Paz(El Alto)" es una sucursal aparte de "La Paz" (mismo departamento,
    // fácil de confundir) — solo debe aparecer para quien administra inventario
    // o para usuarios que efectivamente son de esa sucursal.
    const esDeElAlto = normalize(userSucursal) === normalize("La Paz(El Alto)");
    const puedeVerElAlto = canManageInventory || esDeElAlto;
    const extra = SUCURSALES_ORIGEN_EXTRA.filter(
      (item) => puedeVerElAlto || normalize(item) !== normalize("La Paz(El Alto)")
    );
    const values = [userSucursal, ...extra].filter(Boolean);
    return values.filter((item, index) => values.findIndex((value) => normalize(value) === normalize(item)) === index);
  }, [userSucursal, canManageInventory]);
  const destinos = useMemo(
    () => SUCURSALES_DESTINO.filter((item) => normalize(item) !== normalize(origen)),
    [origen]
  );

  const [tipo, setTipo] = useState("equipo_modem");
  const [estado, setEstado] = useState("Inactivo");
  const [buscar, setBuscar] = useState("");
  const [destino, setDestino] = useState("");
  const [observacion, setObservacion] = useState("");
  const [data, setData] = useState(null);
  const [dataTipo, setDataTipo] = useState("equipo_modem");
  const [notFound, setNotFound] = useState([]);
  const [equiposPage, setEquiposPage] = useState(0);
  const [equiposPageSize, setEquiposPageSize] = useState(30);
  const [selected, setSelected] = useState([]);
  // Accesorios: se traspasan por cantidad, no por serie. { [id]: { cantidad, observacion } }
  const [accInputs, setAccInputs] = useState({});
  const [traspasos, setTraspasos] = useState(null);
  const [traspasoDetalle, setTraspasoDetalle] = useState(null);
  const [buscarTraspaso, setBuscarTraspaso] = useState("");
  const [traspasosPage, setTraspasosPage] = useState(0);
  const [traspasosPageSize, setTraspasosPageSize] = useState(30);
  const [reporteLoading, setReporteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [tab, setTab] = useState(0);
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [traspasoCreado, setTraspasoCreado] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busquedaDialog, setBusquedaDialog] = useState(null);

  const rows = useMemo(() => data?.items || [], [data]);
  const pagedRows = rows;
  const equiposTotal = Number(data?.total ?? rows.length);
  const tableTipo = data?.tipo || dataTipo;
  const tableColumns = useMemo(() => getColumns(tableTipo), [tableTipo]);
  const traspasoCerrado = Boolean(traspasoCreado);

  useEffect(() => {
    if (!origen && userSucursal) {
      setOrigen(userSucursal);
    }
  }, [origen, userSucursal]);

  const getRowSelectionKey = useCallback((row) => {
    const id = getId(row);
    const rowTipo = row?.tipo_equipo || tableTipo || tipo;
    return id ? `${rowTipo}:${id}` : "";
  }, [tableTipo, tipo]);

  const isRowSelectable = useCallback((row) => {
    const estadoRow = normalizeAccess(row?.estado);
    return estadoRow !== "almacen" || canManageInventory;
  }, [canManageInventory]);

  const visibleSelectableRows = useMemo(
    () => pagedRows.filter((row) => getRowSelectionKey(row) && isRowSelectable(row)),
    [getRowSelectionKey, isRowSelectable, pagedRows]
  );
  const allVisibleSelected = visibleSelectableRows.length > 0
    && visibleSelectableRows.every((row) => selected.some((item) => item.key === getRowSelectionKey(row)));
  const someVisibleSelected = visibleSelectableRows.some((row) => selected.some((item) => item.key === getRowSelectionKey(row)));

  const fetchEquipos = useCallback(async (pageOverride = equiposPage, sizeOverride = equiposPageSize, resetPage = false) => {
    if (!origen) {
      setError("El usuario no tiene sucursal configurada.");
      return;
    }

    const nextPage = resetPage ? 0 : pageOverride;
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      if (tipo === "equipo_accesorio") {
        const { data: payloadAcc } = await listarAccesoriosTraspasoCentroEquipos({ sucursal: origen, buscar: buscar.trim() });
        setData(payloadAcc || null);
        setDataTipo("equipo_accesorio");
        setEquiposPage(0);
        setNotFound([]);
        return;
      }
      const { data: payload } = await listarCentroEquipos({
        tipo,
        estado,
        sucursal: origen,
        buscar: buscar.trim(),
        page: nextPage + 1,
        pageSize: sizeOverride,
      });
      setData(payload || null);
      setDataTipo(payload?.tipo || tipo);
      setEquiposPage(nextPage);
      const terms = parseBuscarTerms(buscar);
      const total = Number(payload?.total || 0);
      const items = payload?.items || [];
      setNotFound(total <= items.length ? getNotFoundTerms(terms, items, payload?.tipo || tipo) : []);
    } catch (e) {
      setData(null);
      setNotFound([]);
      setError(e?.response?.data?.message || e?.message || "No se pudo listar equipos para traspaso");
    } finally {
      setLoading(false);
    }
  }, [buscar, equiposPage, equiposPageSize, estado, origen, tipo]);

  const buscarEquipos = () => fetchEquipos(0, equiposPageSize, true);

  const toggle = (row) => {
    const id = getId(row);
    if (!id || !isRowSelectable(row)) return;
    const rowTipo = row?.tipo_equipo || tableTipo || tipo;
    const key = `${rowTipo}:${id}`;
    const item = {
      key,
      tipo: rowTipo,
      id,
      identificador: id,
      modelo: row.modelo,
      estado: row.estado,
      ciudad: row.sucursal || row.ciudad,
      placa: row.placa,
    };
    setSelected((current) => (
      current.some((selectedItem) => selectedItem.key === key)
        ? current.filter((selectedItem) => selectedItem.key !== key)
        : [...current, item]
    ));
  };

  const toggleVisibleRows = () => {
    if (traspasoCerrado || !visibleSelectableRows.length) return;

    setSelected((current) => {
      const visibleKeys = new Set(visibleSelectableRows.map((row) => getRowSelectionKey(row)));
      if (visibleSelectableRows.every((row) => current.some((item) => item.key === getRowSelectionKey(row)))) {
        return current.filter((item) => !visibleKeys.has(item.key));
      }

      const next = [...current];
      visibleSelectableRows.forEach((row) => {
        const id = getId(row);
        const rowTipo = row?.tipo_equipo || tableTipo || tipo;
        const key = `${rowTipo}:${id}`;
        if (isRowSelectable(row) && !next.some((item) => item.key === key)) {
          next.push({
            key,
            tipo: rowTipo,
            id,
            identificador: id,
            modelo: row.modelo,
            estado: row.estado,
            ciudad: row.sucursal || row.ciudad,
            placa: row.placa,
          });
        }
      });
      return next;
    });
  };

  const limpiarSeleccion = () => {
    setSelected([]);
    setAccInputs({});
  };

  const setAccesorioInput = (row, patch) => {
    if (traspasoCerrado) return;
    const id = String(row.id);
    const previo = accInputs[id] || { cantidad: "", observacion: "" };
    const next = { ...previo, ...patch };
    if (patch.cantidad !== undefined) {
      const limpio = String(patch.cantidad).replace(/\D/g, "");
      next.cantidad = limpio === "" ? "" : String(Number(limpio));
    }
    setAccInputs((current) => ({ ...current, [id]: next }));
    const cantidad = Number(next.cantidad);
    const key = `equipo_accesorio:${id}`;
    setSelected((current) => {
      const sinEste = current.filter((item) => item.key !== key);
      if (!(cantidad > 0)) return sinEste;
      return [...sinEste, {
        key,
        tipo: "equipo_accesorio",
        id,
        identificador: row.modelo,
        modelo: row.modelo,
        estado: "",
        ciudad: row.ciudad,
        placa: "",
        cantidad,
        observacion: next.observacion || "",
      }];
    });
  };

  const cambiarOrigen = (value) => {
    setOrigen(value);
    setSelected([]);
    setAccInputs({});
    setData(null);
    setNotFound([]);
    setEquiposPage(0);
    if (normalize(value) === normalize(destino)) {
      setDestino("");
    }
  };

  const abrirBusquedaIdentificador = (identificador) => {
    setBusquedaDialog({
      id: identificador,
      tipo: getBusquedaTipo(tableTipo),
    });
  };

  const nuevoTraspaso = () => {
    setTraspasoCreado(null);
    setSelected([]);
    setAccInputs({});
    setDestino("");
    setObservacion("");
    setData(null);
    setNotFound([]);
    setEquiposPage(0);
    setSuccess("");
    setError("");
  };

  const enviar = async () => {
    if (!destino || !selected.length || traspasoCerrado) return;
    setConfirmOpen(false);

    // La pestaña se abre ya, en el mismo gesto de click: si se abriera después del await
    // el navegador la bloquearía como popup.
    const pdfWindow = window.open("", "_blank");
    if (pdfWindow) {
      pdfWindow.opener = null;
      pdfWindow.document.title = "Generando nota de traspaso...";
      pdfWindow.document.body.innerHTML =
        '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Registrando traspaso y generando la nota...</div>';
    }

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      const { data } = await crearTraspasoCentroEquipos({
        origen,
        destino,
        observacion,
        items: selected.map((item) => ({
          tipo: item.tipo,
          id: item.id,
          ...(item.tipo === "equipo_accesorio" ? { cantidad: item.cantidad, observacion: item.observacion } : {}),
        })),
      });
      setTraspasoCreado(data || {});
      limpiarSeleccion();
      await fetchEquipos(equiposPage, equiposPageSize);
      setSuccess(`Traspaso ${data?.id_traspaso || ""} registrado. Equipos: ${data?.total || selected.length}${data?.cantidad_accesorios ? ` (accesorios: ${data.cantidad_accesorios} unidades)` : ""}`);
      await fetchTraspasos();
      if (pdfWindow && data?.id_traspaso) {
        try {
          const { data: pdf } = await getTraspasoPdfCentroEquipos(data.id_traspaso);
          const pdfUrl = URL.createObjectURL(new Blob([pdf], { type: "application/pdf" }));
          pdfWindow.location.replace(pdfUrl);
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
        } catch {
          pdfWindow.close();
          setError(`Traspaso ${data.id_traspaso} registrado, pero no se pudo generar el PDF. Puedes imprimirlo desde el listado.`);
        }
      }
    } catch (e) {
      if (pdfWindow) pdfWindow.close();
      setError(e?.response?.data?.message || e?.message || "No se pudo registrar el traspaso");
    } finally {
      setSaving(false);
    }
  };

  const fetchTraspasos = useCallback(async (pageOverride = traspasosPage, sizeOverride = traspasosPageSize) => {
    try {
      setReporteLoading(true);
      const { data } = await listarTraspasosCentroEquipos({
        buscar: buscarTraspaso.trim(),
        page: pageOverride + 1,
        pageSize: sizeOverride,
      });
      setTraspasos(data || null);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo listar traspasos");
    } finally {
      setReporteLoading(false);
    }
  }, [buscarTraspaso, traspasosPage, traspasosPageSize]);

  const buscarNotas = () => {
    setTraspasosPage(0);
    fetchTraspasos(0, traspasosPageSize);
  };

  useEffect(() => {
    if (tab === 1 && !traspasos) {
      fetchTraspasos(0, traspasosPageSize);
    }
  }, [fetchTraspasos, tab, traspasos, traspasosPageSize]);

  const verTraspaso = async (id) => {
    try {
      setReporteLoading(true);
      setError("");
      const { data } = await getTraspasoCentroEquipos(id);
      setTraspasoDetalle(data || null);
      setDetalleOpen(true);
    } catch (e) {
      setTraspasoDetalle(null);
      setError(e?.response?.data?.message || e?.message || "No se pudo cargar el detalle del traspaso");
    } finally {
      setReporteLoading(false);
    }
  };

  const imprimirPdf = async (nota) => {
    const id = nota?.id_traspaso;
    if (!id) return;
    try {
      setReporteLoading(true);
      setError("");
      const { data } = await getTraspasoPdfCentroEquipos(id);
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      window.open(pdfUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "No se pudo generar el PDF del traspaso");
    } finally {
      setReporteLoading(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 0.75, borderRadius: 0.75 }}>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        sx={{
          minHeight: 32,
          mb: 0.75,
          borderBottom: "1px solid #e0e0e0",
          "& .MuiTab-root": { minHeight: 32, py: 0.25, px: 1.25, fontSize: 11.5, textTransform: "none" },
        }}
      >
        <Tab label="Registrar traspaso" />
        <Tab label="Listado de traspasos" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 0.75, py: 0 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 0.75, py: 0 }}>{success}</Alert>}

      {tab === 0 && (
        <>
          <Grid container spacing={0.75} alignItems="center">
        <Grid size={{ xs: 12, sm: 6, md: 1.8 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Origen</InputLabel>
            <Select label="Origen" value={origen} onChange={(e) => cambiarOrigen(e.target.value)} disabled={traspasoCerrado}>
              {origenes.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 1.8 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Destino</InputLabel>
            <Select label="Destino" value={destino} onChange={(e) => setDestino(e.target.value)} disabled={traspasoCerrado}>
              {destinos.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 1.7 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Tipo</InputLabel>
            <Select label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)} disabled={traspasoCerrado}>
              {TIPOS.map((item) => (
                <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 1.6 }}>
          <FormControl size="small" fullWidth sx={controlSx}>
            <InputLabel>Estado</InputLabel>
            <Select label="Estado" value={estado} onChange={(e) => setEstado(e.target.value)} disabled={traspasoCerrado || tipo === "equipo_accesorio"}>
              {ESTADOS.map((item) => (
                <MenuItem key={item} value={item}>{item}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <TextField
            size="small"
            label="Buscar"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !traspasoCerrado) buscarEquipos();
            }}
            placeholder="Pega una columna de Excel"
            multiline
            minRows={1}
            maxRows={4}
            disabled={traspasoCerrado}
            fullWidth
            sx={{
              ...controlSx,
              "& .MuiOutlinedInput-root": { minHeight: 30, fontSize: 11.5, alignItems: "flex-start" },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 1 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon fontSize="small" />}
            onClick={buscarEquipos}
            disabled={loading || traspasoCerrado}
            sx={{ minHeight: 30, height: 30, fontSize: 11.5 }}
          >
            Buscar
          </Button>
        </Grid>
          </Grid>

      <TextField
        size="small"
        label="Observacion"
        value={observacion}
        onChange={(e) => setObservacion(e.target.value)}
        disabled={traspasoCerrado}
        multiline
        minRows={2}
        fullWidth
        sx={{ mt: 0.75, "& .MuiInputBase-input": { fontSize: 12 } }}
      />

      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ my: 0.75 }}>
        <Chip size="small" label={`Seleccionados: ${selected.length}`} sx={{ height: 20, fontSize: 11 }} />
        <Chip size="small" label={`Resultados: ${equiposTotal}`} sx={{ height: 20, fontSize: 11 }} />
        <Chip size="small" label={`Pagina: ${rows.length}`} sx={{ height: 20, fontSize: 11 }} />
        <Button
          size="small"
          variant="outlined"
          disabled={!selected.length || traspasoCerrado}
          onClick={limpiarSeleccion}
          sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}
        >
          Limpiar
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {traspasoCerrado && (
          <Button
            variant="outlined"
            color="primary"
            onClick={nuevoTraspaso}
            sx={{ minHeight: 30, height: 30, fontSize: 11.5 }}
          >
            Nuevo traspaso
          </Button>
        )}
        <Button
          variant="contained"
          color="warning"
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <LocalShippingIcon fontSize="small" />}
          disabled={!destino || !selected.length || saving || traspasoCerrado}
          onClick={() => setConfirmOpen(true)}
          sx={{ minHeight: 30, height: 30, fontSize: 11.5 }}
        >
          Enviar traspaso
        </Button>
      </Stack>

          {loading && <LinearProgress sx={{ mb: 0.75 }} />}

      {selected.length > 0 && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 0.75 }}>
          {selected.map((item) => (
            <Chip
              key={item.key}
              size="small"
              label={`${getTipoLabel(item.tipo)}: ${item.identificador}${item.cantidad ? ` (x${item.cantidad})` : ""}`}
              onDelete={() => {
                setSelected((current) => current.filter((selectedItem) => selectedItem.key !== item.key));
                if (item.tipo === "equipo_accesorio") {
                  setAccInputs((current) => {
                    const { [String(item.id)]: _quitado, ...resto } = current;
                    return resto;
                  });
                }
              }}
              sx={{ height: 22, fontSize: 11 }}
            />
          ))}
        </Stack>
      )}

      {notFound.length > 0 && (
        <Alert severity="warning" sx={{ mb: 0.75, py: 0.25 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, display: "block" }}>
            No encontrados ({notFound.length})
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.25 }}>
            {notFound.map((item) => (
              <Chip
                key={item}
                size="small"
                label={item}
                onClick={() => abrirBusquedaIdentificador(item)}
                sx={{ height: 20, fontSize: 10.5, cursor: "pointer" }}
              />
            ))}
          </Stack>
        </Alert>
      )}

          {tableTipo === "equipo_accesorio" ? (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0.5 }}>
              <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.1, px: 0.5, fontSize: 10.5, lineHeight: 1.3, verticalAlign: "middle" } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, width: 36 }}>N°</TableCell>
                    <TableCell sx={{ fontWeight: 800 }}>Modelo</TableCell>
                    <TableCell sx={{ fontWeight: 800, width: 90 }} align="right">Existente</TableCell>
                    <TableCell sx={{ fontWeight: 800, width: 80 }}>Cantidad</TableCell>
                    <TableCell sx={{ fontWeight: 800, width: "32%" }}>Observacion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!rows.length ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                          {loading ? "Cargando..." : "No hay accesorios en esta sucursal. Presiona Buscar."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : rows.map((row, index) => {
                    const input = accInputs[String(row.id)] || { cantidad: "", observacion: "" };
                    return (
                      <TableRow key={row.id} hover selected={Number(input.cantidad) > 0}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{fmt(row.modelo)}</TableCell>
                        <TableCell align="right" sx={{ color: Number(row.buen_estado) < 0 ? "error.main" : undefined }}>
                          {fmt(row.buen_estado)}
                        </TableCell>
                        <TableCell>
                          <Box
                            component="input"
                            value={input.cantidad}
                            onChange={(e) => setAccesorioInput(row, { cantidad: e.target.value })}
                            disabled={traspasoCerrado}
                            placeholder="0"
                            inputMode="numeric"
                            sx={{ ...accInputSx, width: 60 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box
                            component="input"
                            value={input.observacion}
                            onChange={(e) => setAccesorioInput(row, { observacion: e.target.value })}
                            disabled={traspasoCerrado || !(Number(input.cantidad) > 0)}
                            sx={{ ...accInputSx, width: "100%" }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
          <>
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0.5 }}>
        <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.25, px: 0.55, fontSize: 11 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 800, width: 36 }}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && someVisibleSelected}
                  disabled={traspasoCerrado || !visibleSelectableRows.length}
                  onChange={toggleVisibleRows}
                  sx={{ p: 0 }}
                />
              </TableCell>
              {tableColumns.map((column) => (
                <TableCell key={column.label} sx={{ fontWeight: 800 }}>{column.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!rows.length ? (
              <TableRow>
                <TableCell colSpan={tableColumns.length + 1}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Busca equipos de tu sucursal para preparar el traspaso.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : pagedRows.map((row, index) => {
              const id = getId(row);
              const rowTipo = row?.tipo_equipo || tableTipo || tipo;
              const key = `${rowTipo}:${id}`;
              const selectable = isRowSelectable(row);
              return (
                <TableRow
                  key={`${id}-${equiposPage}-${index}`}
                  hover={!traspasoCerrado && selectable}
                  onClick={() => {
                    if (!traspasoCerrado && selectable) toggle(row);
                  }}
                  sx={{
                    cursor: traspasoCerrado || !selectable ? "default" : "pointer",
                    opacity: selectable ? 1 : 0.55,
                  }}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={selected.some((item) => item.key === key)}
                      onChange={() => toggle(row)}
                      disabled={traspasoCerrado || !selectable}
                      sx={{ p: 0 }}
                    />
                  </TableCell>
                  {tableColumns.map((column) => (
                    <TableCell key={column.label}>{fmt(column.value(row))}</TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={equiposTotal}
        page={equiposPage}
        onPageChange={(_, page) => fetchEquipos(page, equiposPageSize)}
        rowsPerPage={equiposPageSize}
        onRowsPerPageChange={(e) => {
          const nextSize = Number(e.target.value);
          setEquiposPageSize(nextSize);
          fetchEquipos(0, nextSize, true);
        }}
        rowsPerPageOptions={[10, 30, 50, 100]}
        labelRowsPerPage="Limite"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        sx={{
          minHeight: 34,
          "& .MuiTablePagination-toolbar": { minHeight: 34, px: 1 },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
            fontSize: 11,
            my: 0,
          },
          "& .MuiTablePagination-select": { fontSize: 11 },
        }}
      />
          </>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          <Paper variant="outlined" sx={{ borderRadius: 0.5, overflow: "hidden" }}>
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ px: 0.75, py: 0.5, bgcolor: "#f7f9fa" }}>
          <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 800 }}>
            Traspasos registrados
          </Typography>
          {reporteLoading && <CircularProgress size={14} />}
          <Box sx={{ flexGrow: 1 }} />
          <TextField
            size="small"
            placeholder="Buscar nota"
            value={buscarTraspaso}
            onChange={(e) => setBuscarTraspaso(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") buscarNotas();
            }}
            sx={{
              width: 180,
              "& .MuiOutlinedInput-root": { height: 24, fontSize: 11 },
              "& .MuiInputBase-input": { py: 0.25, px: 0.75 },
            }}
          />
          <Button size="small" variant="outlined" onClick={buscarNotas} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
            Buscar
          </Button>
        </Stack>
        <TableContainer>
          <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.25, px: 0.55, fontSize: 11 } }}>
            <TableHead>
              <TableRow>
                {["Nota", "Origen", "Destino", "Equipos", "Fecha", "Observacion", "PDF"].map((label) => (
                  <TableCell key={label} sx={{ fontWeight: 800 }}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!(traspasos?.items || []).length ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                      Presiona Buscar para ver los ultimos traspasos.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : traspasos.items.map((nota) => (
                <TableRow key={nota.id_traspaso} hover onClick={() => verTraspaso(nota.id_traspaso)} sx={{ cursor: "pointer" }}>
                  <TableCell>{nota.id_traspaso}</TableCell>
                  <TableCell>{fmt(nota.ciudad)}</TableCell>
                  <TableCell>{fmt(nota.ciudad_des)}</TableCell>
                  <TableCell>{fmt(nota.codigos)}</TableCell>
                  <TableCell>{nota.fecha ? String(nota.fecha).slice(0, 10) : "-"}</TableCell>
                  <TableCell>{fmt(nota.observacion)}</TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Button size="small" startIcon={<PrintIcon fontSize="small" />} onClick={() => imprimirPdf(nota)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
                      PDF
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={traspasos?.total || 0}
          page={traspasosPage}
          onPageChange={(_, page) => {
            setTraspasosPage(page);
            fetchTraspasos(page, traspasosPageSize);
          }}
          rowsPerPage={traspasosPageSize}
          onRowsPerPageChange={(e) => {
            const nextSize = Number(e.target.value);
            setTraspasosPageSize(nextSize);
            setTraspasosPage(0);
            fetchTraspasos(0, nextSize);
          }}
          rowsPerPageOptions={[30, 50, 100]}
          labelRowsPerPage="Filas"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{
            minHeight: 34,
            "& .MuiTablePagination-toolbar": { minHeight: 34, px: 1 },
            "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              fontSize: 11,
              my: 0,
            },
            "& .MuiTablePagination-select": { fontSize: 11 },
          }}
        />
          </Paper>

          <Dialog open={detalleOpen && Boolean(traspasoDetalle)} onClose={() => setDetalleOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle sx={{ py: 1, px: 1.25 }}>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Typography variant="subtitle2" sx={{ fontSize: 12, fontWeight: 800 }}>
                  Detalle traspaso {traspasoDetalle?.nota?.id_traspaso}
                </Typography>
                <Chip size="small" label={`Total: ${traspasoDetalle?.total || 0}`} sx={{ height: 20, fontSize: 11 }} />
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" startIcon={<PrintIcon fontSize="small" />} onClick={() => imprimirPdf(traspasoDetalle?.nota)} sx={{ minHeight: 24, height: 24, fontSize: 10.5 }}>
                  Imprimir PDF
                </Button>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ p: 1 }}>
              <TableContainer>
                <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.25, px: 0.55, fontSize: 11 } }}>
                  <TableHead>
                    <TableRow>
                      {["Cant.", "Tipo", "Identificador", "Estado", "Ciudad", "Usuario", "Observacion"].map((label) => (
                        <TableCell key={label} sx={{ fontWeight: 800 }}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(traspasoDetalle?.items || []).map((item, index) => (
                      <TableRow key={`${item.tipo}-${item.identificador}-${index}`}>
                        <TableCell>{fmt(item.cantidad)}</TableCell>
                        <TableCell>{item.tipo}</TableCell>
                        <TableCell>{item.identificador}</TableCell>
                        <TableCell>{fmt(item.estado)}</TableCell>
                        <TableCell>{fmt(item.ciudad)}</TableCell>
                        <TableCell>{fmt(item.usuario)}</TableCell>
                        <TableCell>{fmt(item.observacion)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </DialogContent>
          </Dialog>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1, px: 1.25 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography variant="subtitle2" sx={{ fontSize: 14, fontWeight: 800 }}>
              Equipos a enviar
            </Typography>
            <Chip size="small" label={`Total: ${selected.length}`} sx={{ height: 20, fontSize: 11 }} />
            <Box sx={{ flexGrow: 1 }} />
            <IconButton size="small" onClick={() => setConfirmOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ mb: 0.75 }}>
            <Chip size="small" label={`Origen: ${origen || "-"}`} sx={{ height: 22, fontSize: 11 }} />
            <Chip size="small" label={`Destino: ${destino || "-"}`} sx={{ height: 22, fontSize: 11 }} />
          </Stack>
          {observacion && (
            <Typography variant="body2" sx={{ fontSize: 11.5, mb: 0.75 }}>
              <strong>Observacion:</strong> {observacion}
            </Typography>
          )}
          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 0.5 }}>
            <Table size="small" sx={{ "& .MuiTableCell-root": { py: 0.3, px: 0.6, fontSize: 11 } }}>
              <TableHead>
                <TableRow>
                  {["Tipo", "Identificador", "Modelo", "Cantidad", "Estado", "Ciudad", "Placa"].map((label) => (
                    <TableCell key={label} sx={{ fontWeight: 800 }}>{label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {selected.map((item) => (
                  <TableRow key={item.key}>
                    <TableCell>{getTipoLabel(item.tipo)}</TableCell>
                    <TableCell>{fmt(item.identificador)}</TableCell>
                    <TableCell>{fmt(item.modelo)}</TableCell>
                    <TableCell>{fmt(item.cantidad || 1)}</TableCell>
                    <TableCell>{fmt(item.estado)}</TableCell>
                    <TableCell>{fmt(item.ciudad)}</TableCell>
                    <TableCell>{fmt(item.placa)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5 }}>
          <Button size="small" onClick={() => setConfirmOpen(false)}>
            Cancelar
          </Button>
          <Button size="small" variant="contained" color="warning" onClick={enviar} disabled={saving}>
            Finalizar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(busquedaDialog)}
        onClose={() => setBusquedaDialog(null)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ py: 0.75, px: 1.25, fontSize: 14, fontWeight: 800 }}>
          Buscar identificador {busquedaDialog?.id || ""}
          <IconButton
            size="small"
            onClick={() => setBusquedaDialog(null)}
            sx={{ position: "absolute", right: 8, top: 5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0.75 }}>
          {busquedaDialog && (
            <BusquedaPorIdentificador
              initialTipo={busquedaDialog.tipo}
              initialBuscar={busquedaDialog.id}
              autoLoadKey={`${busquedaDialog.tipo}-${busquedaDialog.id}`}
              hideSearch
            />
          )}
        </DialogContent>
      </Dialog>
    </Paper>
  );
}
